import { computed, nextTick, reactive, ref } from 'vue'
import { parsePersonalNumber } from '@staff/shared'
import type { TopupPurpose } from '@staff/shared'
import {
  MAX_BORROWERS,
  MAX_CHILDREN,
  type ApplicantForm,
  type BoolString,
  type CapitalAssetForm,
  type CreateApplicationFormType,
  type EmploymentStatus,
  type HouseholdCarForm,
  type HouseholdChildForm,
  type MaritalStatus,
  type OtherLoanForm,
  type OtherPropertyForm,
  type PropertyType,
  type RequestedProduct,
} from '../applicationFormConfig'

export const createEmptyApplicant = (): ApplicantForm => ({
  userId: '',
  personalNumber: '',
  email: '',
  phoneNumber: '',
  maritalStatus: undefined,
  employmentStatus: undefined,
  employer: '',
  employerOrganisationNumber: '',
  employedStartDate: '',
  temporaryEmployedEndDate: '',
  monthlyIncome: undefined,
  monthlyIncomeCurrency: 'SEK',
})

const createEmptyHouseholdChild = (): HouseholdChildForm => ({
  age: undefined,
  custody: '50',
  maintenanceSupportIncome: 'false',
  maintenanceSupportCost: 'false',
})

const createEmptyOtherLoan = (): OtherLoanForm => ({
  type: undefined,
  amount: undefined,
})

const createEmptyOtherProperty = (): OtherPropertyForm => ({
  type: undefined,
  keepProperty: undefined,
  loanAmount: undefined,
  monthlyFee: undefined,
  ownershipShare: undefined,
  taxValue: undefined,
  isPropertyOld: undefined,
})

const createEmptyHouseholdCar = (): HouseholdCarForm => ({
  ownershipType: undefined,
  monthlyCost: undefined,
})

const createEmptyCapitalAsset = (): CapitalAssetForm => ({
  type: undefined,
  amount: undefined,
})

export const createInitialFormState = () => ({
  type: 'newLoanApplication' as CreateApplicationFormType,
  debtId: '',
  comment: '',

  userId: '',
  brokerExternalId: '',
  deletedApplicantPersonalNumbers: [] as string[],
  personalNumber: '',
  email: '',
  phoneNumber: '',
  maritalStatus: undefined as MaritalStatus | undefined,
  employmentStatus: undefined as EmploymentStatus | undefined,
  employer: '',
  employerOrganisationNumber: '',
  employedStartDate: '',
  temporaryEmployedEndDate: '',
  monthlyIncome: undefined as number | undefined,
  monthlyIncomeCurrency: 'SEK' as const,
  coApplicants: [] as ApplicantForm[],

  amountAppliedFor: undefined as number | undefined,
  isHighPrio: undefined as BoolString | undefined,
  isNewProduction: 'false' as BoolString,
  requestedFeatures: [] as RequestedProduct[],
  children: [] as HouseholdChildForm[],
  cars: [] as HouseholdCarForm[],
  capitalAssets: [] as CapitalAssetForm[],
  collateralType: undefined as PropertyType | undefined,
  monthlyFee: undefined as number | undefined,
  street: '',
  streetNo: '',
  city: '',
  zip: undefined as number | undefined,
  apartmentNbr: '',
  apartmentNbrBRF: '',
  rooms: undefined as number | undefined,
  floors: undefined as number | undefined,
  nbrOfFloors: undefined as number | undefined,
  balcony: undefined as BoolString | undefined,
  elevator: undefined as BoolString | undefined,
  size: undefined as number | undefined,
  taxValue: undefined as number | undefined,
  isInsured: undefined as BoolString | undefined,
  insuranceCompany: '',
  insuranceCompanyOtherName: '',
  isFullyInsured: undefined as BoolString | undefined,
  coverageAmount: undefined as number | undefined,
  excessAmount: undefined as number | undefined,

  propertyEstimation: undefined as number | undefined,
  cashContribution: undefined as number | undefined,
  propertyTypeEstimation: undefined as PropertyType | undefined,
  sizeEstimation: undefined as number | undefined,
  monthlyFeeEstimation: undefined as number | undefined,
  kommunEstimation: '',

  topupPurpose: undefined as TopupPurpose | undefined,
  topupPurposeOther: '',
  otherLoans: [] as OtherLoanForm[],
  otherProperties: [] as OtherPropertyForm[],
})

const form = reactive(createInitialFormState())

// The API checks a draft save against fewer rules than a submit: some fields may be empty in a
// draft but must be filled in before submit. Their rules check validatingFor === 'submit'.
type ValidationMode = 'draft' | 'submit'
const validatingFor = ref<ValidationMode>('draft')

// Fields re-validate when their rules change, so the mode is set and rendered before the group
// validates.
const validateFor = async (mode: ValidationMode, validate: (callback: () => void) => unknown, callback: () => void) => {
  validatingFor.value = mode
  await nextTick()
  await validate(callback)
}

export type CreateApplicationFormState = typeof form

const isTopupApplication = computed(() => form.type === 'topupLoanApplication')
const borrowerCount = computed(() => 1 + form.coApplicants.length)
// The borrowers on a topup come from the mortgage being raised - they can't be changed here.
const canAddBorrower = computed(() => !isTopupApplication.value && borrowerCount.value < MAX_BORROWERS)
const canAddChild = computed(() => form.children.length < MAX_CHILDREN)
const collateralMonthlyFeeLabel = computed(() =>
  form.collateralType === 'apartment' ? 'Driftskostnad (inkl månadsavgift)' : 'Driftskostnad',
)

const addCoApplicant = () => {
  if (canAddBorrower.value) form.coApplicants.push(createEmptyApplicant())
}
const removeCoApplicant = (index: number) => {
  const [applicant] = form.coApplicants.splice(index, 1)
  if (
    applicant?.userId &&
    applicant.personalNumber &&
    !form.deletedApplicantPersonalNumbers.includes(applicant.personalNumber)
  ) {
    form.deletedApplicantPersonalNumbers.push(applicant.personalNumber)
  }
}
const addOtherLoan = () => form.otherLoans.push(createEmptyOtherLoan())
const removeOtherLoan = (index: number) => form.otherLoans.splice(index, 1)
const addOtherProperty = () => form.otherProperties.push(createEmptyOtherProperty())
const removeOtherProperty = (index: number) => form.otherProperties.splice(index, 1)
const addHouseholdChild = () => {
  if (canAddChild.value) form.children.push(createEmptyHouseholdChild())
}
const removeHouseholdChild = (index: number) => form.children.splice(index, 1)
const addHouseholdCar = () => form.cars.push(createEmptyHouseholdCar())
const removeHouseholdCar = (index: number) => form.cars.splice(index, 1)
const addCapitalAsset = () => form.capitalAssets.push(createEmptyCapitalAsset())
const removeCapitalAsset = (index: number) => form.capitalAssets.splice(index, 1)

// A draft can only be saved once every borrower has a valid personal number - the
// backend keys applicants on it.
const canSaveDraft = computed(
  () =>
    Boolean(parsePersonalNumber(form.personalNumber)) &&
    form.coApplicants.every((applicant) => Boolean(parsePersonalNumber(applicant.personalNumber))),
)

export const useApplicationFormData = () => {
  return {
    form,
    isTopupApplication,
    canSaveDraft,
    validatingFor,
    validateFor,
    canAddBorrower,
    canAddChild,
    collateralMonthlyFeeLabel,
    addCoApplicant,
    removeCoApplicant,
    addOtherLoan,
    removeOtherLoan,
    addOtherProperty,
    removeOtherProperty,
    addHouseholdChild,
    removeHouseholdChild,
    addHouseholdCar,
    removeHouseholdCar,
    addCapitalAsset,
    removeCapitalAsset,
  }
}
